import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { dashboardAPI } from '../../../services/dashboardService';

/**
 * UsersTab Component
 * Displays user statistics with User Type filtering
 */
const UsersTab = ({ filters, onFilterChange, onViewDetailed }) => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const userTypeCategories = [
        { value: 'PET_GUARDIAN', label: 'Pet Guardians' },
        { value: 'VETERINARIAN', label: 'Veterinarians' },
        { value: 'SERVICE_PROVIDER', label: 'Service Providers' },
        { value: 'ADMIN', label: 'Administrators' },
        { value: 'ORGANIZATION_ADMIN', label: 'Organization Admins' }
    ];

    useEffect(() => {
        loadUserStats();
    }, [filters]);

    const loadUserStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardAPI.getUserStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load user stats:', err);
            setError(err.message || 'Failed to load user statistics');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="w-full">
                <div className="panel-container" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="loading-skeleton" style={{ width: '40px', height: '40px' }}></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button onClick={loadUserStats} className="retry-button">
                    Retry
                </button>
            </div>
        );
    }

    const getStatusColor = (type) => {
        // Simple mapping, can be expanded
        return 'blue';
    };

    return (
        <div className="w-full">
            <div className="flex justify-end mb-6">
                <button
                    onClick={onViewDetailed}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors font-medium text-sm"
                >
                    View Detailed Analytics <ArrowRight size={16} />
                </button>
            </div>

            {/* User Stats Cards */}
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-info)' }}>
                    <div className="flex flex-col">
                        <h3 className="stat-label">Total Users</h3>
                        <p className="stat-value">
                            {(stats?.totalUsers || 0).toLocaleString()}
                        </p>
                        <p className="stat-trend trend-up">
                            <span className="mr-1">↑</span> {stats?.userGrowth || 0}% from previous period
                        </p>
                    </div>
                </div>

                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-success)' }}>
                    <div className="flex flex-col">
                        <h3 className="stat-label">Active Users</h3>
                        <p className="stat-value">
                            {(stats?.activeUsers || 0).toLocaleString()}
                        </p>
                        <p className="stat-trend" style={{ color: 'var(--color-text-secondary)' }}>
                            {stats?.activePercentage || 0}% of total users
                        </p>
                    </div>
                </div>

                <div className="stat-card" style={{ borderLeft: '4px solid var(--color-primary)' }}>
                    <div className="flex flex-col">
                        <h3 className="stat-label">New Users</h3>
                        <p className="stat-value">
                            {(stats?.newUsers || 0).toLocaleString()}
                        </p>
                        <p className="stat-trend" style={{ color: 'var(--color-text-secondary)' }}>
                            In selected time range
                        </p>
                    </div>
                </div>
            </div>

            {/* User Type Breakdown */}
            <div className="panel-container">
                <h3 className="panel-title">User Type Breakdown</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {stats?.usersByType && Object.entries(stats.usersByType).map(([type, count]) => {
                        const total = stats.totalUsers || 1;
                        const percentage = ((count / total) * 100).toFixed(1);
                        const category = userTypeCategories.find(c => c.value === type);

                        return (
                            <div key={type} className="progress-container">
                                <div className="progress-label-row">
                                    <span>{category?.label || type}</span>
                                    <span style={{ color: 'var(--color-text-secondary)' }}>
                                        {count.toLocaleString()} ({percentage}%)
                                    </span>
                                </div>
                                <div className="progress-track">
                                    <div
                                        className="progress-fill"
                                        style={{ width: `${percentage}%` }}
                                    ></div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Recent Users Table */}
            {stats?.recentUsers && stats.recentUsers.length > 0 && (
                <div className="panel-container">
                    <h3 className="panel-title">Recent Registrations</h3>
                    <div className="table-container">
                        <table className="dashboard-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Type</th>
                                    <th>Registered</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.recentUsers.slice(0, 10).map((user, idx) => (
                                    <tr key={user.id || idx}>
                                        <td style={{ fontWeight: 500 }}>
                                            {user.name || 'N/A'}
                                        </td>
                                        <td style={{ color: 'var(--color-text-secondary)' }}>
                                            {user.email || 'N/A'}
                                        </td>
                                        <td>
                                            <span className="status-badge blue">
                                                {user.type || 'User'}
                                            </span>
                                        </td>
                                        <td style={{ color: 'var(--color-text-secondary)' }}>
                                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersTab;
